import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type InappKeyboardLayout = 'text' | 'email' | 'numeric';

export interface InappKeyboardRequest {
  layout: InappKeyboardLayout;
  initialValue: string;
  placeholder?: string;
  // Called whenever the value changes while typing
  onChange: (val: string) => void;
  // Called on confirm (Done/OK)
  onConfirm?: (val: string) => void;
  // Called on cancel/close
  onCancel?: () => void;
  // Optional label for the header/title
  label?: string;
  // Optional validator: when provided, OK button stays disabled until valid
  validator?: (val: string) => boolean;
  // Optional callback when confirm attempted while invalid
  onInvalid?: (val: string) => void;
}

export interface InappKeyboardState {
  visible: boolean;
  request?: InappKeyboardRequest;
  value: string;
  caps: boolean;
  symbols: boolean;
  valid: boolean;
}

@Injectable({ providedIn: 'root' })
export class InappKeyboardService {
  private stateSubject = new BehaviorSubject<InappKeyboardState>({
    visible: false,
    value: '',
    caps: false,
    symbols: false,
    valid: true,
  });

  state$ = this.stateSubject.asObservable();

  open(request: InappKeyboardRequest) {
    const initialValue = request.initialValue ?? '';
    const valid = this.computeValidity(request, initialValue);
    this.stateSubject.next({
      visible: true,
      request,
      value: initialValue,
      caps: false,
      symbols: false,
      valid,
    });
  }

  close(cancelled = false) {
    const st = this.stateSubject.getValue();
    if (st.request) {
      if (cancelled) {
        st.request.onCancel && st.request.onCancel();
      }
    }
    this.stateSubject.next({
      visible: false,
      value: '',
      caps: false,
      symbols: false,
      valid: true,
    });
  }

  confirm() {
    const st = this.stateSubject.getValue();
    if (st.request) {
      if (!st.valid) {
        st.request.onInvalid && st.request.onInvalid(st.value);
        return;
      }
      st.request.onConfirm && st.request.onConfirm(st.value);
    }
    this.close();
  }

  setValue(val: string) {
    const st = this.stateSubject.getValue();
    if (!st.visible) return;
    const request = st.request!;
    request.onChange(val);
    const valid = this.computeValidity(request, val);
    this.stateSubject.next({ ...st, value: val, valid });
  }

  backspace() {
    const st = this.stateSubject.getValue();
    if (!st.visible) return;
    if (!st.value) return;
    this.setValue(st.value.slice(0, -1));
  }

  clear() {
    this.setValue('');
  }

  toggleCaps() {
    const st = this.stateSubject.getValue();
    if (!st.visible) return;
    this.stateSubject.next({ ...st, caps: !st.caps });
  }

  toggleSymbols() {
    const st = this.stateSubject.getValue();
    if (!st.visible) return;
    this.stateSubject.next({ ...st, symbols: !st.symbols });
  }

  typeChar(ch: string) {
    const st = this.stateSubject.getValue();
    if (!st.visible) return;
    const next = (st.value || '') + ch;
    this.setValue(next);
  }

  private computeValidity(request: InappKeyboardRequest, value: string): boolean {
    if (!request.validator) {
      return true;
    }
    try {
      return request.validator(value);
    } catch {
      return false;
    }
  }
}
